import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Button, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import img from "../../images/logo.svg";
import { fetchCandidateEmailByID } from "../../redux-store/candidateSlicer";
import candidateService from "../../service-api/candidateService";
import CandidateForm from "../candidate-form/CandidateForm";
import { ALLOWED_INTERNAl_EMAILS } from "../../constants/constants";

// simple sessionStorage helper
const storage = {
  set: (key, value) => sessionStorage.setItem(key, JSON.stringify(value)),
  get: (key, defaultValue) => {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  },
  remove: (key) => sessionStorage.removeItem(key),
};

const AuthenticateEmailCandidateForm = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { candidateEmail } = useSelector((state) => state.candidates);
  const [candidateId, setCandidateId] = useState(null);

  const [loading, setLoading] = useState({
    verifyEmail: false,
    verifyOtp: false,
    resendOtp: false,
  });

  // load from sessionStorage first
  const [email, setEmail] = useState(() =>
    storage.get("email", { value: "", verified: false })
  );
  const [userOtp, setUserOtp] = useState(() =>
    storage.get("userOtp", { value: "", verified: false })
  );

  const [error, setError] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);

  const inputRefs = useRef([]);

  let correctOtp = "123456"; // Correct OTP
  const otpLength = correctOtp.length;

  const setButtonLoading = (button, value) => {
    setLoading((prev) => ({ ...prev, [button]: value }));
  };

  // persist email & otp into sessionStorage

  const checkLinkExpiry = async () => {
    try {
      await candidateService.getCandidateFeedbackToken();
      const res = await candidateService.getRecruitmentFormExpiryByToken(
        new URLSearchParams(window.location.search).get("token")
      );
      setCandidateId(
        res.data.value.length > 0
          ? res.data.value[0]._itl_candidateid_value
          : null
      );
      const expiryTime =
        res.data.value.length > 0 ? res.data.value[0].itl_expirytime : null;
      if (new Date() > new Date(expiryTime)) {
        setLinkExpired(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    checkLinkExpiry();
  }, []);

  useEffect(() => {
    storage.set("email", email);
  }, [email]);

  useEffect(() => {
    storage.set("userOtp", userOtp);
  }, [userOtp]);

  const verifyOTP = async () => {
    if (userOtp.value.length !== otpLength) {
      setError({ otp: "Please enter the complete OTP." });
      return;
    }

    setButtonLoading("verifyOtp", true);
    try {
      const response = await candidateService.verifyCandidateOTP({
        id: candidateId,
        email: candidateEmail.itl_emaili,
        otp: userOtp.value,
      });

      if (response && userOtp.value.length === otpLength) {
        setUserOtp({ value: userOtp.value, verified: true });
        setError(false);
      } else {
        setError({ otp: "Incorrect OTP. Please try again." });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setButtonLoading("verifyOtp", false);
    }
  };

  const verifyEmail = async (e) => {
    e.preventDefault();
    setButtonLoading("verifyEmail", true);

    const allowedInternalEmails = ALLOWED_INTERNAl_EMAILS

    const isAllowedEmail =
      email.value.toLowerCase() === candidateEmail.toLowerCase() ||
      allowedInternalEmails.includes(email.value.toLowerCase());

    if (isAllowedEmail) {
      const payload = { email: email.value, id: candidateId };

      try {
        const response = await candidateService.sendCandidateOtp(payload);

        setEmail((prev) => ({ ...prev, verified: true }));
        setError(false);
      } catch (error) {
        console.error("OTP Sending Failed:", error);
        setError((prev) => ({
          ...prev,
          email: "Failed to send OTP. Please try again.",
        }));
      } finally {
        setLoading(false);
      }
    } else {
      setError((prev) => ({
        ...prev,
        email: "Unknown Email Address.",
      }));
      setButtonLoading("verifyEmail", false);
    }
  };

  useEffect(() => {
    const getTokenAndFetchCandidate = async () => {
      try {
        dispatch(fetchCandidateEmailByID(candidateId));
      } catch (error) {
        console.error(
          "Error getting token or fetching candidate email:",
          error
        );
      }
    };

    if (candidateId) {
      getTokenAndFetchCandidate();
    }
  }, [candidateId]);

  // Resend OTP UseEffect & Function
  useEffect(() => {
    if (email.verified && resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (resendTimer === 0) {
      setResendDisabled(false);
    }
  }, [resendTimer, email.verified]);

  const resendOTP = async () => {
    setButtonLoading("resendOtp", true);

    try {
      const payload = { email: email.value, id: candidateId };
      await candidateService.sendCandidateOtp(payload);
      // setOtpExpireTimer(60);
      // setOtpExpired(false);
    } catch (error) {
      toast.error("Failed to resend OTP. Try again.");
      console.error("Resend OTP Error:", error);
    } finally {
      setResendDisabled(true);
      setResendTimer(60);
      setButtonLoading("resendOtp", false);
      toast.success("OTP Resent Successfully!");
    }
  };

  const handleOTPChange = (index, value) => {
    setError((prev) => ({ ...prev, otp: null }));
    if (value.length > 1) return;

    let otpArray = userOtp.value.split("");
    otpArray[index] = value;

    setUserOtp({ ...userOtp, value: otpArray.join("") });

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    setUserOtp({ value: pastedData, verified: false });

    pastedData.split("").forEach((digit, idx) => {
      inputRefs.current[idx].value = digit;
    });

    const nextIndex = pastedData.length < 6 ? pastedData.length : 5;
    inputRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !userOtp.value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && userOtp.value.length === 6) {
      verifyOTP();
    }
  };

  useEffect(() => {
    const loggedInUserId = JSON.parse(localStorage.getItem("user"));

    if (loggedInUserId) {
      setIsLoggedIn(true);
      setUserOtp((prev) => ({ ...prev, verified: true }));
    }
  }, []);

  if (linkExpired) {
    return (
      <div className="candidate-feedback-email-auth-wrapper">
        <div className="container d-flex h-100">
          <div className="row w-100">
            <div className="col-md-6 m-auto d-flex flex-column align-items-center">
              <div className="candidate-email-authenticate-wrapper text-center">
                <img src={img} alt="" className="mb-4" />
                <h1 className="sub-heading mb-4 text-danger">
                  This link has expired or modified. Please contact HR for a new
                  link.
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {!userOtp.verified ? (
        <section className="candidate-feedback-email-auth-wrapper">
          <div className="container d-flex h-100">
            <form
              className="w-100 d-flex justify-content-center"
              onSubmit={email.verified ? verifyOTP : verifyEmail}
            >
              <div className="row w-100">
                <div className="col-md-6 m-auto d-flex flex-column align-items-center">
                  <div className="candidate-email-authenticate-wrapper">
                    <img src={img} alt="" className="mb-4" />

                    <div className="sub-heading mb-4">
                      Please enter your email address to verify.
                    </div>

                    <div className="form-group w-100 mb-4">
                      <input
                        disabled={email.verified}
                        type="email"
                        placeholder="e.g. mikeross123@gmail.com"
                        className="form-control w-100 placeholder-light"
                        onChange={(e) =>
                          setEmail((prev) => ({
                            ...prev,
                            value: e.target.value,
                          }))
                        }
                      />

                      {error.email && (
                        <Typography.Text
                          type="danger"
                          className="mb-4 position-absolute"
                        >
                          <CloseCircleOutlined /> {error.email}
                        </Typography.Text>
                      )}

                      {email.verified && (
                        <Typography.Text
                          type="success"
                          className="mb-4 position-absolute"
                        >
                          <CheckCircleOutlined /> Email Verified.
                        </Typography.Text>
                      )}
                    </div>

                    {!email.verified && (
                      <Button
                        type="primary"
                        className="mt-3 m-auto custom-btn"
                        onClick={verifyEmail}
                        disabled={loading.verifyEmail}
                      >
                        Verify Email{" "}
                        {loading.verifyEmail && (
                          <Spinner animation="border" size="sm" />
                        )}
                      </Button>
                    )}
                    {/* </div> */}

                    {email.verified && (
                      <div className="d-flex align-items-center justify-content-center flex-column">
                        <div className="sub-heading mb-3 mt-3">
                          Enter OTP sent on your email address
                        </div>
                        <div className="mb-3">
                          <Row md={6}>
                            {[0, 1, 2, 3, 4, 5].map((item, index) => (
                              <Col className="mb-3" key={index}>
                                <input
                                  ref={(el) => (inputRefs.current[index] = el)}
                                  type="number"
                                  className={`form-control text-center w-45-h-45 row-gap-2 ${error.otp && "border border-danger"
                                    }`}
                                  value={userOtp.value[index] || ""}
                                  onChange={(e) =>
                                    handleOTPChange(index, e.target.value)
                                  }
                                  onKeyDown={(e) => handleKeyDown(index, e)}
                                  onPaste={handleOTPPaste}
                                  autoFocus={index === 0}
                                />
                              </Col>
                            ))}
                          </Row>
                        </div>
                        {error.otp && (
                          <Typography.Text type="danger" className="mb-1">
                            {error.otp}
                          </Typography.Text>
                        )}

                        <div className="text-center">
                          <Button
                            type="link"
                            disabled={resendDisabled || loading.resendOtp}
                            onClick={resendOTP}
                          >
                            {resendDisabled
                              ? `Resend OTP in ${resendTimer}s`
                              : "Resend OTP"}
                            {loading.resendOtp && (
                              <Spinner animation="border" size="sm" />
                            )}
                          </Button>

                          {/* <div
                            className="text-muted"
                            style={{ fontSize: "14px" }}
                          >
                            {otpExpired
                              ? "OTP expired. Please request a new one."
                              : `OTP expires in ${otpExpireTimer}s`}
                          </div> */}
                        </div>

                        <Button
                          type="primary"
                          className="mt-3 custom-btn"
                          onClick={verifyOTP}
                          disabled={
                            userOtp.value.length !== otpLength ||
                            loading.verifyOtp
                          }
                        >
                          Verify OTP{" "}
                          {loading.verifyOtp && (
                            <Spinner animation="border" size="sm" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </section>
      ) : (
        <CandidateForm isLoggedIn={isLoggedIn} candidateId={candidateId} />
      )}
    </React.Fragment>
  );
};

export default AuthenticateEmailCandidateForm;
